import React, { useState, useRef, useEffect } from "react";
import { Button } from "@netapp/bxp-design-system-react";
import { useLangNavigate } from "../../utils/langNavigation";
import { useTranslation } from "react-i18next";
import "./UniversalNavBar.scss";
import { NetAppLogoIcon } from "@netapp/bxp-design-system-react/icons/netapp-services";

const NavBar = () => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language === "ja" ? t("japanese.other") : t("english.other"));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useLangNavigate();

  // Refs for outside-click detection
  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);

  const handleLanguageChange = (code) => {
    setLanguage(code === "ja" ? t("japanese.other") : t("english.other"));
    i18n.changeLanguage(code);
    setIsDropdownOpen(false);
    
    // Update URL with language parameter (hide default English param)
    const url = new URL(window.location);
    if (code === "en") {
      url.searchParams.delete("lang");
    } else {
      url.searchParams.set("lang", code);
    }
    const updatedSearch = url.searchParams.toString();
    const newRelativeUrl = `${url.pathname}${updatedSearch ? `?${updatedSearch}` : ""}${url.hash}`;
    window.history.pushState({}, "", newRelativeUrl);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((v) => !v);
  };

  const languages = [
    { code: "en", label: t("english.other") },
    { code: "ja", label: t("japanese.other") },
  ];

  const navLinks = [
    { text: t("security_advisories.other"), path: "/advisory" },
    { text: t("policy.other"), path: "/policy" },
    { text: t("resources.other"), path: "/resources" },
    { text: t("certifications.other"), path: "/certifications" },
    { text: t("bulletins.other"), path: "/bulletins" },
    { text: t("contactUs.other"), path: "/report" },
  ];

  // Sync language state when i18n language changes
  useEffect(() => {
    setLanguage(i18n.language === "ja" ? t("japanese.other") : t("english.other"));
  }, [i18n.language, t]);

  // Close mobile menu on outside click or Escape
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handlePointer = (event) => {
      const menuEl = mobileMenuRef.current;
      const toggleEl = mobileToggleRef.current;

      const clickedInsideMenu = menuEl && menuEl.contains(event.target);
      const clickedToggle = toggleEl && toggleEl.contains(event.target);

      if (!clickedInsideMenu && !clickedToggle) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="universal-navbar">
      <div className="headerFrame">
        <div className="greyNavBar">
          <div className="language-dropdown">
            <button
              className="dropdown-toggle"
              onClick={toggleDropdown}
              aria-expanded={isDropdownOpen}
            >
              <img src="/language.svg" alt="Language Icon" className="nav-icon" />
              {language}
            </button>
            {isDropdownOpen && (
              <div className="dropdown-menu" role="menu">
                <div className="dropdown-header">{t("language.other")}</div>
                <div className="dropdown-content">
                  {languages.map((lang) => (
                    <div
                      key={lang.code}
                      className="dropdown-item"
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <span>{lang.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="blueHeaderBar">
          <div className="titleBar">
            <NetAppLogoIcon className="netappHeaderLogo" />
            <Button
              variant="text"
              className="securityPortalTitle"
              onClick={() => navigate("/")}
            >
              {t("securityPortal.other", "Security Portal")}
            </Button>
          </div>

          {/* Desktop nav (hidden on mobile via CSS) */}
          <div className="blueNavigationBar">
            {navLinks.map((link, index) => (
              <Button
                key={index}
                variant="text"
                className="navButton"
                onClick={() => navigate(link.path)}
              >
                {link.text}
              </Button>
            ))}
          </div>

          <div>
            {/* Hamburger toggle (visible on mobile via CSS) */}
            <button
              ref={mobileToggleRef}
              className={`mobileMenuToggle ${isMobileMenuOpen ? "is-open" : ""}`}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
            >
              <span className="hamburger-icon" aria-hidden="true"></span>
              <span className="sr-only">
                {isMobileMenuOpen ? "Close menu" : "Open menu"}
              </span>
            </button>
          </div>

          {/* Mobile nav (vertical list; only visible on mobile) */}
          {isMobileMenuOpen && (
            <div className="mobileNavMenu" id="mobile-nav" ref={mobileMenuRef}>
              {navLinks.map((link, index) => (
                <Button
                  key={`m-${index}`}
                  variant="text"
                  className="navButton"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate(link.path);
                  }}
                >
                  {link.text}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavBar;